import { useState } from 'react';
import { Accordion, Card as BSCard, Dropdown } from 'react-bootstrap';
import { AccordionHeader } from 'components/DesignSystems/Accordion/AccordionHeader';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { AddLayerFolderModal } from './AddLayerFolderModal';
import { useUscgMapContext } from './UscgMapContext';
import { ExternalLayerItem } from './ExternalLayerItem';
import { InternalLayerItem } from './InternalLayerItem';
import { LayerDetailModal } from './LayerDetailModal';
import { ArcGisConnectButton } from './ArcGisConnectButton';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type {
  InternalLayer,
  LayerWithNodeMeta,
} from 'hooks/internalLayer/types';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useDeleteInternalLayerMutation } from 'hooks/internalLayer/useDeleteInternalLayerMutation';
import { useDeleteExternalLayerMutation } from 'hooks/externalLayer/useDeleteExternalLayerMutation';
import { useIsCommandCenter } from './utils/isCommandCenter';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { IncidentSelect } from './IncidentSelect';
import { ViewLayerFolderModal } from './ViewLayerFolderModal';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';
import { useDeleteLayerNodeMutation } from 'hooks/layerNode/useDeleteLayerNodeMutation';
import type { LayerGroupDto, LayerFolderItemDto } from 'hooks/layerNode/types';
import { OnAddLayer } from './utils/constants';

function isExternalLayer(
  layer: ExternalLayer | InternalLayer
): layer is ExternalLayer {
  return (
    'arcgisItemId' in layer || 'serviceUrl' in layer || 'itemType' in layer
  );
}

const isFolderItemVisible =
  (
    isInternalLayerVisible: (id: string) => boolean,
    isExternalLayerVisible: (id: string) => boolean
  ) =>
  (item: LayerFolderItemDto) => {
    const { layer } = item;
    return isExternalLayer(layer)
      ? isExternalLayerVisible(layer.id)
      : isInternalLayerVisible(layer.id);
  };

const areAllFolderItemsVisible = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean
) => {
  const { items } = group;
  if (items.length === 0) return false;
  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  return items.every(checkVisible);
};

const isFolderGroupIndeterminate = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean
) => {
  const { items } = group;
  if (items.length === 0) return false;
  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  const someVisible = items.some(checkVisible);
  const allVisible = items.every(checkVisible);
  return someVisible && !allVisible;
};

const handleFolderGroupVisibilityToggle = (
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean,
  toggleInternalLayerVisibility: (id: string) => void,
  toggleExternalLayerVisibility: (id: string) => void
) => {
  const { items } = group;
  if (items.length === 0) return;

  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  const allVisible = items.every(checkVisible);
  const targetVisible = !allVisible;

  items.forEach(({ layer }) => {
    const currentlyVisible = isExternalLayer(layer)
      ? isExternalLayerVisible(layer.id)
      : isInternalLayerVisible(layer.id);

    if (currentlyVisible !== targetVisible) {
      if (isExternalLayer(layer)) {
        toggleExternalLayerVisibility(layer.id);
      } else {
        toggleInternalLayerVisibility(layer.id);
      }
    }
  });
};

export function ViewLayersPanel({
  onAddLayer,
  onOpenReviewQueue,
}: {
  onAddLayer: OnAddLayer;
  onOpenReviewQueue: () => void;
}) {
  const groupGuid = useGroupGuid();

  const {
    zoomToGraphic,
    zoomToExternalLayer,
    zoomToInternalLayer,
    isInternalLayerVisible,
    isExternalLayerVisible,
    toggleInternalLayerVisibility,
    toggleExternalLayerVisibility,
    incidentFilterId: incidentId,
    setIncidentFilterId,
  } = useUscgMapContext();

  const deleteInternalLayer = useDeleteInternalLayerMutation();
  const deleteExternalLayer = useDeleteExternalLayerMutation();
  const deleteLayerNode = useDeleteLayerNodeMutation();
  const isCommandCenter = useIsCommandCenter();

  const [searchTerm, setSearchTerm] = useState('');
  const [layerDetailModal, setLayerDetailModal] =
    useState<LayerWithNodeMeta | null>(null);
  const [selectedFolderGroup, setSelectedFolderGroup] =
    useState<LayerGroupDto | null>(null);

  const { data: layerNodeGroups } = useListLayerNodes({
    filter: {
      groupGuid: groupGuid ?? '',
      ...(incidentId ? { incidentIds: [incidentId] } : {}),
      ...(searchTerm && { search: searchTerm }),
    },
  });

  const renderLayerItem = (changeArg: LayerWithNodeMeta) => {
    const { layer, type } = changeArg;

    return type === 'internal' ? (
      <InternalLayerItem
        key={`internal-${layer.id}`}
        layer={layer}
        onDelete={() =>
          deleteInternalLayer.mutate({ internalLayerId: layer.id })
        }
        onOpenInNewClick={() => setLayerDetailModal(changeArg)}
        onZoomToLayer={() => zoomToInternalLayer({ internalLayerId: layer.id })}
      />
    ) : (
      <ExternalLayerItem
        key={`external-${layer.id}`}
        layer={layer}
        onDelete={() =>
          deleteExternalLayer.mutate({ externalLayerId: layer.id })
        }
        onOpenInNewClick={() => setLayerDetailModal(changeArg)}
        onZoomToLayer={() => zoomToExternalLayer({ externalLayerId: layer.id })}
        onZoomToGraphic={(graphic) => zoomToGraphic({ graphic })}
      />
    );
  };

  return (
    <div className="d-flex flex-column gap-3 px-2 pt-2 pb-5">
      <Card>
        <div className="d-flex justify-content-between">
          <StylishNewSearchBar
            placeholder="Search..."
            value={searchTerm}
            onChangeSearchTerm={(val) => setSearchTerm(val.trim())}
          />

          <div className="d-flex gap-2">
            <AddLayerFolderModal />
            <Dropdown style={{ height: '100%' }}>
              <Dropdown.Toggle
                className="button button--primary pe-auto"
                style={{ height: '100%' }}
                id="add-layer-dropdown"
              >
                <SharedIcon iconName="add" size="md" />
                Add Layer
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item
                  onClick={() => {
                    onAddLayer('dhs');
                  }}
                >
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add from DHS</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    onAddLayer('default');
                  }}
                >
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add Default Layer</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    onAddLayer('external');
                  }}
                >
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add from ArcGIS</span>
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onAddLayer('internal')}>
                  <SharedIcon iconName="add" size="sm" />
                  <span>Add Internal Layer</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Card>

      <div className="row g-2">
        <Card>
          <IncidentSelect
            value={incidentId}
            onChange={setIncidentFilterId}
            // incident filter is disabled in Incident Workspace
            isDisabled={!isCommandCenter}
          />
        </Card>
      </div>

      <div className="d-flex gap-2">
        <ArcGisConnectButton />
        {!isCommandCenter && (
          <StylishNewButton
            className="button--secondary"
            onClick={onOpenReviewQueue}
          >
            Review queue
          </StylishNewButton>
        )}
      </div>

      <div className="d-grid mt-2 gap-2">
        {layerNodeGroups?.data.map((group: LayerGroupDto, index: number) => {
          const folderLabel = group.folder?.name ?? 'Other';
          const eventKey = group.folder?.id ?? `other-${index}`;

          return (
            <Accordion
              key={eventKey}
              defaultActiveKey={group.items.length > 0 ? eventKey : undefined}
            >
              <BSCard className="accordion--card border border-neutral-500 bg-neutral-800">
                <AccordionHeader eventKey={eventKey} hasIcon>
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <StylishNewCheckbox
                        className="mt-0"
                        checked={areAllFolderItemsVisible(
                          group,
                          isInternalLayerVisible,
                          isExternalLayerVisible
                        )}
                        indeterminate={isFolderGroupIndeterminate(
                          group,
                          isInternalLayerVisible,
                          isExternalLayerVisible
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderGroupVisibilityToggle(
                            group,
                            isInternalLayerVisible,
                            isExternalLayerVisible,
                            toggleInternalLayerVisibility,
                            toggleExternalLayerVisibility
                          );
                        }}
                      />
                      <span className="fw-semibold">{folderLabel}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <StylishNewButton
                        className="button--icon bg-bglight-hover border-0 bg-transparent"
                        disabled={!group.folder || group.items.length > 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!group.folder) return;

                          deleteLayerNode.mutate({
                            id: group.folder.id,
                            groupGuid: group.folder.groupGuid,
                          });
                        }}
                        data-testid="delete-layer-folder-btn"
                      >
                        <SharedIcon iconName="delete" size="sm" />
                      </StylishNewButton>
                      <StylishNewButton
                        className="button--icon bg-bglight-hover border-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFolderGroup(group);
                        }}
                      >
                        <SharedIcon iconName="open_in_new" size="sm" />
                      </StylishNewButton>
                    </div>
                  </div>
                </AccordionHeader>
                <Accordion.Collapse eventKey={eventKey}>
                  <BSCard.Body className="p-0">
                    <ul className="list-unstyled d-grid mb-0 gap-2 p-2">
                      {group.items.length === 0 ? (
                        <li className="text-muted small">No layers</li>
                      ) : (
                        group.items.map((item) => {
                          return renderLayerItem({
                            ...(isExternalLayer(item.layer)
                              ? {
                                  type: 'external',
                                  layer: item.layer as ExternalLayer,
                                }
                              : {
                                  type: 'internal',
                                  layer: item.layer as InternalLayer,
                                }),

                            ...item,
                          });
                        })
                      )}
                    </ul>
                  </BSCard.Body>
                </Accordion.Collapse>
              </BSCard>
            </Accordion>
          );
        })}
      </div>

      <LayerDetailModal
        key={layerDetailModal?.layer.id ?? 'no-layer'}
        show={layerDetailModal !== null}
        onClose={() => setLayerDetailModal(null)}
        layerWithType={layerDetailModal}
      />
      <ViewLayerFolderModal
        show={selectedFolderGroup !== null}
        onClose={() => setSelectedFolderGroup(null)}
        folderGroup={selectedFolderGroup}
        allGroups={layerNodeGroups?.data ?? []}
      />
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-bgdark rounded border border-neutral-500 p-3">
      {children}
    </div>
  );
}
